import store from '../../store';
import stompManager from './stompManager';
import topicManager from './topicManager';

let hasInitialized = false;
let stopProfileWatcher = null;


const defaultCallbacks = {
  onConnect: () => console.log('[STOMP] 연결 성공'),
  onDisconnect: () => console.log('[STOMP] 연결 종료'),
  onError: (error) => console.error('[STOMP] 연결 오류', error)
};


const initStompService = (customCallbacks = {}) => {
  if (hasInitialized) {
    return stompServiceApi;
  }

  const callbacks = { ...defaultCallbacks, ...customCallbacks };
  const profile = store.getters['user/getUserProfile'];
  console.log('[STOMP] initStompService profile:', profile?.companySeq);
  connectAndSubscribeTopic(profile?.companySeq, `/topic/events/${profile?.companySeq}`, null, callbacks);


  stopProfileWatcher = store.watch(
    (state, getters) => getters['user/getUserProfile']?.companySeq,
    (companySeq, previousCompanySeq) => {
      if (companySeq === previousCompanySeq) {
        return;
      }

      if (companySeq) {
        connectAndSubscribeTopic(companySeq, `/topic/events/${companySeq}`, null, callbacks);
      } else {
        stompManager.cleanUpConnection();
      }
    }
  );

  hasInitialized = true;
  return stompServiceApi;
};

/**
 * 회사 ID와 특정 토픽 경로를 기반으로 STOMP 연결을 생성하고 토픽을 구독합니다.
 * 연결과 구독을 하나의 트랜잭션으로 처리합니다.
 * @param {string} companySeq - 회사 ID
 * @param {string} topicPath - 구독할 토픽 경로 (예: /topic/events/123)
 * @param {Function} handler - 토픽 메시지 핸들러 함수 (payload, topicPath) => void
 * @param {Object} callbacks - 연결 콜백 함수들 (onConnect, onDisconnect, onError)
 * @param {Object} options - 연결 옵션 (retryDelay, maxRetryCount 등)
 * @returns {Object|null} STOMP 클라이언트 또는 null
 */
const connectAndSubscribeTopic = (companySeq, topicPath, handler, callbacks = {}, options = {}) => {
  if (!companySeq) { 
    console.warn('[STOMP] companySeq가 없어 연결을 시작하지 않습니다.');
    return null;
  }

  if (!topicPath) {
    console.warn('[STOMP] topicPath가 없어 연결을 시작하지 않습니다.');
    return null;
  }

  const wrappedCallbacks = {
    ...callbacks,
    onConnect: () => {
      const subscription = topicManager.subscribeTopic(topicPath, handler);
      
      if (subscription) {
        console.log(`[STOMP] 토픽 구독 성공: ${topicPath}`);
      } else {
        console.warn(`[STOMP] 토픽 구독 실패: ${topicPath}`);
      }
      if (callbacks.onConnect) {
        callbacks.onConnect();
      }
    }
  };
  return stompManager.createConnection(companySeq, wrappedCallbacks, options);
};


const disposeStompService = () => {
  if (typeof stopProfileWatcher === 'function') {
    stopProfileWatcher();
    stopProfileWatcher = null;
  }
  stompManager.cleanUpConnection();
  hasInitialized = false;
};

/**
 * 회사 ID를 기반으로 STOMP 연결을 생성하고 기본 토픽을 구독합니다.
 * 연결과 구독을 하나의 트랜잭션으로 처리합니다.
 * @param {string} companySeq - 회사 ID
 * @param {Object} callbacks - 연결 콜백 함수들 (onConnect, onDisconnect, onError)
 * @param {Object} options - 연결 옵션 (retryDelay, maxRetryCount 등)
 * @returns {Object|null} STOMP 클라이언트 또는 null
 */
const connectWithCompany = (companySeq, callbacks = {}, options = {}) => {
  if (!companySeq) {
    console.warn('[STOMP] companySeq가 없어 연결을 시작하지 않습니다.');
    return null;
  }
  const wrappedCallbacks = {
    ...callbacks,
    onConnect: () => {
      const companyTopicPath = `/topic/events/${companySeq}`;
      const subscription = topicManager.subscribeTopic(companyTopicPath, null);
      if (subscription) {
        console.log(`[STOMP] 기본 토픽 구독 성공: ${companyTopicPath}`);
      } else {
        console.warn(`[STOMP] 기본 토픽 구독 실패: ${companyTopicPath}`);
      }

      // 원본 onConnect 콜백 호출
      if (callbacks.onConnect) {
        callbacks.onConnect();
      }
    }
  };
  return stompManager.createConnection(companySeq, wrappedCallbacks, options);
};



const stompServiceApi = {
  init: initStompService,
  dispose: disposeStompService,
  connectWithCompany,
  connectAndSubscribeTopic,
  createConnection: stompManager.createConnection,
  disconnect: stompManager.disconnect,
  isConnected: stompManager.isConnected,
  getConnectionState: stompManager.getConnectionState,
  cleanUpConnection: stompManager.cleanUpConnection,
  createNewConnection: stompManager.createNewConnection,
  attemptReconnect: stompManager.attemptReconnect,
  getStompClient: stompManager.getStompClient,
  subscribeTopic: topicManager.subscribeTopic,
  unsubscribeTopic: topicManager.unsubscribeTopic
};

export { disposeStompService, stompServiceApi };
export default initStompService;